import React, { useEffect, useState } from 'react';
import { Container, Form, Button } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import instance from '../../api/api';
import { IRegUser } from '../../interfaces/authentication';
import IPage from '../../interfaces/page';
import { IRegistrationResponse } from '../../interfaces/responses';
import * as EmailValidator from 'email-validator';
import { INotification, NotificationType } from '../../interfaces/notification';
import Notification, { notificationImages } from '../../components/notifications/notification.component';
import simirity_logo from "../../assets/images/logo2_with_tagline_horizontal.svg";
import registration_intro from "../../assets/images/registration-intro.webp";
import i18n from "../../i18n";
import { useTranslation } from 'react-i18next';
import SubscriptionReferalWidget from '../../components/account/subscription/subscription-referral-registration-widget.component';
import SubscriptionFamilyVipWidget from '../../components/account/subscription/subscription-family-vip-registration-widget.component';
import DemoNotification from '../../components/notifications/demo.notification.component';
import Icon from '@mdi/react';
import { mdiBookOpenPageVariant, mdiImageMultiple, mdiHeart, mdiEye, mdiEyeOff } from '@mdi/js';
import SignInWithGoogleButton from '../../components/authentication/sign-in-with-google-button';

const defaultUserObject : IRegUser = {
    email:'',
    password:'',
    language:'en'
}

/**
 * Registration page for user authentication
 * @param props : unused for now
 * @returns void
 */
const Registration: React.FunctionComponent<IPage> = props => {

    const [ userData, setUserData ] = useState<IRegUser>({...defaultUserObject});
    const [ inputError, setInputError] = useState<IRegUser>({...defaultUserObject});
    const [ showPassword, setShowPassword ] = useState<boolean>(false);
    const [ showEmailPasswordForm, setShowEmailPasswordForm ] = useState<boolean>(false);

    const [ referral, setReferal ] = useState<string>("");
    const [ invitation, setInvitation ] = useState<string>("");
    const [ inviter, setInviter ] = useState<string>("");
    const [ familyVip, setFamilyVip ] = useState<string>("");
    const [ validReferal, setValidReferal ] = useState<boolean>(false);
    const [ validFamilyVip, setValidFamilyVip ] = useState<boolean>(false);
    const [ validInvitation, setValidInvitation ] = useState<boolean>(false);

    const [ notification, setNotification ] = useState<INotification>({icon:'', title:'', text:'', success:NotificationType.problem});

    const navigate = useNavigate();

    const { t } = useTranslation();

    const { lang } = useParams();

    const demo = window.__RUNTIME_CONFIG__.REACT_APP_DEMO === "false" ? false : true;

    const location = useLocation();
    const state = location.state;

    useEffect(() => {
        if(lang) {
            i18n.changeLanguage(lang);
        }
        if(state) {
            if(state.id) {
                setInvitation(state.id);
                validateInvitation(state.id);
                //getInvitation(state.id)
            }

            if(state.referral) {
                setReferal(state.referral);
                validateReferal(state.referral);
            }

            if(state.familyVip) {
                setFamilyVip(state.familyVip);
                validateFamilyVip(state.familyVip);
            }
        }
    }, [])

    //Set error to empty string after a while.
    function resetNotification() {
        setNotification({icon:'', title:'', text:'', success:NotificationType.problem})
    }

    // Set the register button to disabled if one of the input fields is empty
    const [isButtonDisabled, setButtonDisabled] = useState<boolean>(true);

    useEffect(() => {
        let dataIsMissing:boolean = false

        Object.entries(userData).forEach(([key, value]) => {
            if (!value.length) {
                dataIsMissing = true
            }
        })

        setButtonDisabled(dataIsMissing)
    }, [userData])

    async function validateReferal(id:string) {
        try {
            const response = await instance.get("/subscriptions/public/" + id);
            if(response.data.valid) {
                setValidReferal(true);
            }
        }
        catch(err) {
            console.log('err', err)
        }
    }

    async function validateFamilyVip(id:string) {
        try {
            const response = await instance.get("/subscriptions/family-vip/public/" + id);
            if(response.data.length) {
                setValidFamilyVip(true);
            }
        }
        catch(err) {
            console.log('err', err)
        }
    }

    async function validateInvitation(id:string) {
        try {
            const response = await instance.get("/invitation/public/" + id);

            if(response.data.length) {
                setValidInvitation(true);
                setInviter(response.data);
            }
        }
        catch(err) {
            setNotification({...notification, icon:notificationImages.crying, title:t('Error'), text:t("Invitation link expired or invalid")})
        }
    }    


    /**
     * Login request
     * response:
     *      - 200: token
     *      - 403: Unauthorized
     */
    async function handleRegistration(evt: React.FormEvent) {
        evt.preventDefault();

        let isValidData:boolean = true

        //Email validation
        if(!EmailValidator.validate(userData.email)) {
            setNotification({...notification, icon:notificationImages.crying, title:t('Error'), text:t('Invalid e-mail format')})
            return
        }

        if(userData.password.length < 8 || !(/\d/.test(userData.password))) {
            setNotification({...notification, icon:notificationImages.crying, title:t('Error'), text:t("Password has to be at least 8 character, and must contain a number")})
            return
        }

        if (isValidData === true) {
            try {

                // add language if there is any
                const sendUserData = lang ? {...userData, language:lang} :  userData;

                let response = await instance.post<IRegistrationResponse>('/auth/registration', { userData: sendUserData });

                if(response && response.data.user && response.data.user._id) {

                    if(response.data.user._id) {
                        localStorage.setItem('userid', response.data.user._id);
                    }
                    navigate(`/verification${lang ? "/"+lang : ''}`, { state: {email: response.data.user.email, referral:referral, familyVip: familyVip, invitation: invitation }});

                } else if(response && !response.data.verified && response.data.user.email) {
                    // already existing but not verified user
                    navigate(`/verification${lang ? "/"+lang : ''}`, { state:{ email: response.data.user.email, referral:referral, familyVip: familyVip, invitation: invitation }});
                } else {
                    // already existing user
                    setNotification({
                        ...notification,
                        icon: notificationImages.crying,
                        title: t("Error"),
                        text: (
                            <>
                                <p>{t("Email already registered")}</p>
                                <p className="forgot-password-link" onClick={() => navigate("/forgotten-password")}>
                                    {t("Forgotten password?")}
                                </p>
                            </>
                        ),
                    })
                }
            }

            catch(err) {
                setNotification({...notification, icon:notificationImages.crying, title:t('Error'), text:t("Registration failed. Please notify us at info@simirity.com")})
            }
        }
    }

    /*
    // obsolete
    async function getInvitation(id:string) {
        const invitation = await instance.get('/invitation/' + id);
        if(invitation.data) {
           setUserData(prev => {return {...prev, email:invitation.data.email}})
        }
    }
    */        

    return(
        <Container className="registration-container">
            <img src={simirity_logo} alt="simirity" className="logo" />
            <p className="title">{t("Join For Free")}</p>
                {demo ? <DemoNotification />
                :
                <Form className="registration-form" onSubmit={handleRegistration}>
                    <div className="intro">
                        <p><Icon size={0.8} path={mdiBookOpenPageVariant} color="#4A90E2" /> {t("Discover untold family stories")}</p>
                        <p><Icon size={0.8} path={mdiImageMultiple} color="#E67E22" /> {t("Preserve memories with media")}</p>
                        <p><Icon size={0.8} path={mdiHeart} color="#E74C3C" /> {t("Unite family in conversations")}</p>
                    </div>
                    <img src={registration_intro} alt="Registration intro" className="registration-intro-image" />

                    {invitation.length && validInvitation ?
                        <p className="subtitle">{t(`${t("Register to connect with")} ${inviter}`)}</p>
                        :
                        null
                    }

                    {referral.length && validReferal ?
                        <p className="subtitle">{t(`${t("Register and get a 1-month family subscription for free")} ${inviter}`)}</p>
                        :
                        null
                    }                                   

                    <SignInWithGoogleButton context="register" invitation={invitation} referral={referral} familyVip={familyVip}/>

                    {!showEmailPasswordForm ? (
                        <div className="google-auth-section">
                            <div className="divider">
                                <span>{t("Or")}</span>
                            </div>
                            <button
                                type="button"
                                className="email-signup-toggle-button"
                                onClick={() => setShowEmailPasswordForm(true)}
                            >
                                {t("Sign up with Email")}
                            </button>
                        </div>
                    ) : (
                        <>
                            <Form.Group className="group email">
                                <input required autoFocus={true} type="email" className={inputError.email.length ? "string-input warning": "string-input"} placeholder={t("Email")} value={userData.email} onChange={(evt) => setUserData(prev => {return {...prev, email:evt.target.value}})} />
                                <Form.Label className="top-label">{t("Email")}</Form.Label>
                                <Form.Label className="bottom-label">{t("Invited to Simirity? Use your invitation's email address.")}</Form.Label>
                                {inputError.email.length ? <p className="warning-message">{inputError.email}</p> : null}
                            </Form.Group>

                            <Form.Group className="group password">
                                <div className="password-input">
                                    <input required type={showPassword ? "text" : "password"} className={inputError.password.length ? "string-input warning": "string-input"} placeholder={t("Password")} value={userData.password} onChange={(evt) => setUserData(prev => {return {...prev, password:evt.target.value}})} />
                                    <svg onClick={() => setShowPassword(prev => !prev)}>
                                        <Icon path={showPassword ? mdiEye : mdiEyeOff} />
                                    </svg>
                                </div>
                                <Form.Label className="top-label">{t("Password")}</Form.Label>
                                <Form.Label className="bottom-label">{t("At least 8 characters, with letters and numbers")}</Form.Label>
                                {inputError.password.length ? <p className="warning-message">{inputError.password}</p> : null}
                            </Form.Group>

                            <div className="button-group">
                                <Button disabled={isButtonDisabled} className="registration-button default-button" variant="primary" type="submit">{t("Register")}</Button>
                            </div>                            
                        </>
                    )}                    

                    <p>{t("By registering, you confirm that you are 18+ years old and agree to our ")}
                        <a href="https://www.simirity.com/terms-conditions/" className="underlined-link-button" rel="noreferrer" target="_blank">{t("Terms & Conditions")}</a>
                        {t(" and ")}
                        <a href="https://www.simirity.com/data-privacy/" className="underlined-link-button" rel="noreferrer" target="_blank">{t("Privacy Policy")}</a>
                    </p>

                    {/*referral.length && validReferal
                        ?
                            <SubscriptionReferalWidget lang={lang ? lang : ''}/>
                        :
                            (referral.length && !validReferal )
                        ?
                            <p>{t("Expired referral")}</p> :
                        null
                    */}

                    {familyVip.length && validFamilyVip
                        ?
                            <SubscriptionFamilyVipWidget />
                        :
                            (familyVip.length && !validFamilyVip )
                        ?
                            <p>t{("Expired Family-VIP")}</p> :
                        null
                    }

                    {notification.title.length ? <Notification data={notification} close={() => resetNotification()}/> : null}

                    {/*<div className="tos">
                        <p>
                            <a href="https://www.simirity.com/terms-conditions/" className="underlined-link-button" rel="noreferrer" target="_blank">{t("Terms & Conditions")}</a> and <a href="https://www.simirity.com/data-privacy/" className="underlined-link-button" rel="noreferrer" target="_blank">{t("Privacy Policy")}</a>
                        </p>
                    </div>*/}

                </Form>}
        </Container>
    );
};

export default Registration;
